import type { ProjectReportsGatewayPort } from "@/application/project-reports/ports/ProjectReportsGatewayPort";
import type {
  CreateProjectReportDTO,
  ProjectReportDTO,
  UpdateProjectReportDTO,
} from "@/application/project-reports/dtos/ProjectReportDTO";
import {
  createProjectReport,
  deleteProjectReport,
  getProjectReport,
  getProjectReports,
  updateProjectReport,
} from "@/lib/projectReportsApi";
import type {
  CreateProjectReportRequest,
  ProjectReport as ApiProjectReport,
  UpdateProjectReportRequest,
} from "@/types/project-reports";

export class ProjectReportsApiAdapter implements ProjectReportsGatewayPort {
  async create(projectId: string, data: CreateProjectReportDTO): Promise<ProjectReportDTO> {
    const payload = this.mapCreateDTOToRequest(data);
    const response = await createProjectReport(projectId, payload);
    return this.mapResponseToDTO(response);
  }

  async getById(projectId: string, reportId: string): Promise<ProjectReportDTO> {
    const response = await getProjectReport(projectId, reportId);
    return this.mapResponseToDTO(response);
  }

  async list(projectId: string): Promise<ProjectReportDTO[]> {
    const response = await getProjectReports(projectId);
    return response.map((item) => this.mapResponseToDTO(item));
  }

  async update(
    projectId: string,
    reportId: string,
    data: UpdateProjectReportDTO
  ): Promise<ProjectReportDTO> {
    const payload = this.mapUpdateDTOToRequest(data);
    const response = await updateProjectReport(projectId, reportId, payload);
    return this.mapResponseToDTO(response);
  }

  async delete(projectId: string, reportId: string): Promise<void> {
    await deleteProjectReport(projectId, reportId);
  }

  private mapResponseToDTO(response: ApiProjectReport): ProjectReportDTO {
    return {
      id: response.id,
      promptsIds: response.prompts_ids ?? [],
      recordsIds: response.records_ids ?? [],
      projectId: response.project_id,
      category: response.category ?? null,
      title: response.title,
      context: response.context ?? null,
      contextExpand: response.context_expand ?? null,
      status: response.status,
      content: response.content ?? null,
      isPublic: response.is_public ?? false,
      isArchived: response.is_archived ?? false,
      isDeleted: response.is_deleted ?? false,
      presetId: response.preset_id ?? null,
      reportLanguage: response.report_language ?? null,
      createdAt: response.created_at,
      updatedAt: response.updated_at,
    };
  }

  private mapCreateDTOToRequest(data: CreateProjectReportDTO): CreateProjectReportRequest {
    // Explicitly handle preset_id and report_language to ensure they are included
    // even if they are null (for optional fields, we want to send null explicitly)
    const mapped: CreateProjectReportRequest = {
      prompts_ids: data.promptsIds,
      records_ids: data.recordsIds,
      category: data.category ?? null,
      title: data.title,
      context: data.context ?? null,
      context_expand: data.contextExpand ?? null,
      status: data.status,
      content: data.content ?? null,
      is_public: data.isPublic ?? false,
      is_archived: data.isArchived ?? false,
      // Explicitly include preset_id and report_language, even if they are null
      // This ensures they are sent to the server
      preset_id: data.presetId !== undefined ? data.presetId : null,
      report_language: data.reportLanguage !== undefined ? data.reportLanguage : null,
    };
    
    // cleanUndefined only removes undefined values, not null
    // So null values will be included in the request
    return this.cleanUndefined<CreateProjectReportRequest>(mapped as unknown as Record<string, unknown>);
  }

  private mapUpdateDTOToRequest(data: UpdateProjectReportDTO): UpdateProjectReportRequest {
    return this.cleanUndefined<UpdateProjectReportRequest>({
      prompts_ids: data.promptsIds,
      records_ids: data.recordsIds,
      category: data.category,
      title: data.title,
      context: data.context,
      context_expand: data.contextExpand,
      status: data.status,
      content: data.content,
      is_public: data.isPublic,
      is_archived: data.isArchived,
      is_deleted: data.isDeleted,
      preset_id: data.presetId,
      report_language: data.reportLanguage,
      reasoning_mode: data.reasoningMode,
    });
  }

  private cleanUndefined<T>(payload: Record<string, unknown>): T {
    const result: Record<string, unknown> = {};
    Object.entries(payload).forEach(([key, value]) => {
      if (value !== undefined) {
        result[key] = value;
      }
    });
    return result as T;
  }
}
